import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getActiveTeam,
  getProjectData,
  getAvailableTeam,
  addDeveloper,
} from "../../AdminServices/AdminProjectService";
import { getRoleTypes } from "../../AdminServices/AdminMetadataService";
import frameBg from "../../../../Assets/FrameBGDashboard.png";
import { Users, ShieldCheck } from "lucide-react";
import AddDeveloperModal from "../AdminModals/AddDeveloperModal";
import NotificationAlert from "../../../Alerts/NotificationAlert";
 import { useNavigate } from "react-router-dom";

const AdminAddDeveloper = () => {
   const navigate = useNavigate();
  const { projectUID } = useParams();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [roleTypes, setRoleTypes] = useState([]);
  const [alert, setAlert] = useState(null);
  const [unassignedTeam, setUnassignedTeam] = useState([]);

  // store selected role dropdown per developer
  const [selectedRoles, setSelectedRoles] = useState({});

  useEffect(() => {
    loadTeam();
    loadProjectName();
    loadRoleTypes();
    loadAvailableTeam();
  }, [projectUID]);

  const loadProjectName = async () => {
    try {
      const res = await getProjectData();
      if (res.data.status === "Success") {
        const project = res.data.data.find((p) => p.projectUID === projectUID);
        if (project) setProjectName(project.projectName);
      }
    } catch (error) {
       if (error.response?.status === 400) {
      navigate("/bad-request", {
        state: {
          error: error.response?.data?.message || "Bad request (400)",
        },
      });
      return;
    }
      console.error("Error loading project name", error);
    }
  };

  const loadTeam = async () => {
    try {
      const response = await getActiveTeam(projectUID);
      setTeam(response.data.teamMemebers || []);
    } catch (err) {
       if (err.response?.status === 400) {
      navigate("/bad-request", {
        state: {
          error: err.response?.data?.message || "Bad request (400)",
        },
      });
      return;
    }
      console.error("Error loading project team:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableTeam = async () => {
    try {
      const response = await getAvailableTeam(projectUID);
      setUnassignedTeam(response.data.avaliableMembers || []);
    } catch (err) {
       if (err.response?.status === 400) {
      navigate("/bad-request", {
        state: {
          error: err.response?.data?.message || "Bad request (400)",
        },
      });
      return;
    }
      console.error("Error loading available team:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadRoleTypes = async () => {
    try {
      const res = await getRoleTypes();
      if (res.data.status === "Success") {
        setRoleTypes(res.data.data);
      }
    } catch (err) {
       if (err.response?.status === 400) {
      navigate("/bad-request", {
        state: {
          error: err.response?.data?.message || "Bad request (400)",
        },
      });
      return;
    }
      console.error("Error loading roles", err);
    }
  };

  const handleRoleChange = (userUID, roleId) => {
    setSelectedRoles((prev) => ({
      ...prev,
      [userUID]: roleId,
    }));
  };

  const handleAssign = async (dev) => {
    const roleId = selectedRoles[dev.userUID];

    if (!roleId) {
      alert("Please select a role!");
      return;
    }

    const payload = {
      projectUID,
      userUID: dev.userUID,
      roleId,
    };

    try {
      const res = await addDeveloper(payload);

      if (res.data.status === "Success") {
        setAlert({
          type: "success",
          message: res.data.message || "Developer Assigned Successfully",
        });

        // Refresh both lists
        loadTeam();
        loadAvailableTeam();
      } else {
        setAlert({
          type: "error",
          message: res.data.message || "Assignment Failed!",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Error Assigning developer",
      });
    }
  };

  if (loading)
    return (
      <div className="flex justify-center pt-20">
        <p className="text-lg font-semibold text-[#0061A4] animate-pulse">
          Loading team...
        </p>
      </div>
    );

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      {/* HEADER */}
      <div
        className="relative rounded-lg overflow-hidden shadow-md text-white mb-10"
        style={{
          backgroundImage: `url(${frameBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="p-8 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-semibold drop-shadow">
              Project Team : {projectName}
            </h2>
          </div>
        </div>
      </div>

      {/* TEAM SPLIT LAYOUT */}
      <div className="px-6 md:px-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* LEFT — Unassigned Developers */}
          <div className="bg-[#f6fafe] border border-[#C7E2FF] rounded-xl p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-[#0061A4] mb-4 flex items-center gap-2">
              <Users size={20} /> Unassigned Developers
            </h3>

            <div className="space-y-3">
              {unassignedTeam.map((dev) => (
                <div
                  key={dev.userUID}
                  className="bg-white p-3 rounded-lg border border-[#C7E2FF] shadow-sm flex justify-between items-center"
                >
                  <span className="font-medium text-[#0061A4]">{dev.name}</span>

                  <div className="flex items-center gap-2">
                    <select
                      className="border rounded-md text-sm px-2 py-1 border-[#C7E2FF] text-[#0061A4]"
                      value={selectedRoles[dev.userUID] || ""}
                      onChange={(e) =>
                        handleRoleChange(dev.userUID, e.target.value)
                      }
                    >
                      <option value="">Select Role</option>
                      {roleTypes.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>

                    <button
                      className="bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white px-3 py-1 rounded-md text-sm shadow"
                      onClick={() => handleAssign(dev)}
                    >
                      Assign
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Assigned Developers */}
          <div className="bg-[#f6fafe] border border-[#C7E2FF] rounded-xl p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-[#0061A4] mb-4 flex items-center gap-2">
              <Users size={20} /> Assigned Developers
            </h3>

            {team.length === 0 ? (
              <p className="text-gray-600 text-lg mt-4">
                No developers assigned yet.
              </p>
            ) : (
              <div className="space-y-3">
                {team.map((member) => (
                  <div
                    key={member.userUID}
                    className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm border border-[#C7E2FF]"
                  >
                    <span className="font-medium text-[#0061A4]">
                      {member.name}
                    </span>

                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-[#0061A4] border border-[#C7E2FF] flex items-center gap-1">
                      <ShieldCheck size={12} /> {member.role}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AddDeveloperModal
        show={showModal}
        onClose={() => setShowModal(false)}
        projectUID={projectUID}
        onAdded={() => {
          loadTeam();
          loadAvailableTeam();
        }}
      />

      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default AdminAddDeveloper;
